import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { WaitlistDialog } from "../components/WaitlistDialog";
import { Seo } from "../components/Seo";
import { ArrowRight, Sparkles } from "lucide-react";

const BOOK_COVER = "/brand/book-cover.webp";
const ALFRED = "/brand/alfred.webp";

export default function Books() {
  const [book, setBook] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { type: "book" } })
      .then((r) => setBook(r.data.find((p) => p.title === "Welcome to Adulthood") || r.data[0] || null))
      .catch(() => {});
  }, []);

  const learnMore = book ? `/store/${book.id}` : "/store?type=book";

  return (
    <div data-testid="books-page">
      <Seo
        title="Books — Welcome to Adulthood"
        description="Welcome to Adulthood: A Practical Guide to Building the Life You Want. A step-by-step guide for young adults on money, housing, career, education, and more. Available September 1, 2026."
        path="/books"
      />
      <section className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-gold text-sm uppercase tracking-[0.3em]">Books</span>
          <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-4">Books by Laura Combs</h1>
          <p className="text-charcoal/70 mt-5 leading-relaxed">
            Thoughtful, practical books that meet you where you are and equip you for the road ahead.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center bg-white rounded-3xl border border-gold/30 shadow-sm p-8 sm:p-12">
          {/* Cover */}
          <div className="flex justify-center">
            <div className="bg-ivory rounded-2xl p-4 shadow-md">
              <img
                src={BOOK_COVER}
                alt="Welcome to Adulthood — full cover"
                className="w-full max-w-sm object-contain drop-shadow-xl"
                fetchpriority="high"
                decoding="async"
                data-testid="books-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div>
            <span className="inline-flex items-center gap-2 bg-gold/15 border border-gold/40 rounded-full px-4 py-1.5 text-sm text-forest font-medium" data-testid="books-availability">
              <Sparkles className="w-4 h-4 text-gold" />
              Available September 1, 2026
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-5 leading-tight">Welcome to Adulthood</h2>
            <p className="text-charcoal/70 italic mt-3 text-lg">A Practical Guide to Building the Life You Want</p>
            <p className="text-3xl text-charcoal font-medium mt-5" data-testid="books-price">
              ${book ? book.price.toFixed(2) : "49.99"}
            </p>
            <p className="text-charcoal/70 mt-6 leading-relaxed">
              A step-by-step guide for young adults navigating real life — money, housing, education,
              career, protection, and goals. With warm, practical guidance (and Alfred the Owl) by
              your side, you have everything you need to build a strong, confident, purposeful life.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">
              <WaitlistDialog source="books-page">
                <Button className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8 h-12 gap-2 transition-colors" data-testid="books-waitlist-btn">
                  Join the Waitlist
                </Button>
              </WaitlistDialog>
              <Link to={learnMore}>
                <Button variant="outline" className="rounded-full border-forest/30 text-forest hover:bg-forest/5 px-8 h-12 gap-2" data-testid="books-learnmore-btn">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-4 mt-10 pt-8 border-t border-gold/20">
              <img src={ALFRED} alt="Alfred the Owl" className="w-16 h-16 object-contain" loading="lazy" decoding="async" />
              <p className="text-charcoal/60 text-sm leading-relaxed">
                Guided by <span className="text-forest font-medium">Alfred</span> — the friendly
                mentor who walks you through every big decision.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
